/**
 * EditCarrierModal
 *
 * Comprehensive editor for Carrier details and structure (Divisions/Contacts).
 * Uses a Tabs layout to separate general info from structural management.
 *
 * Hooks used:
 * - useGetCarrierQuery: Fetch carrier details
 * - useListDivisionsQuery: Fetch divisions for carrier
 * - useUpdateCarrierMutation: Update carrier name/address/type
 * - useCreateDivisionMutation: Create a new division
 * - useListDivisionHandlesQuery: Fetch handles linked to a division
 * - useAddHandleMutation: Create a new handle (pass divisionId to auto-link)
 * - useArchiveHandleMutation: Soft-delete a handle
 */

import {
  Building2,
  ChevronDown,
  ChevronRight,
  LayoutList,
  Mail,
  MapPin,
  Phone,
  Plus,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  type CarrierDivision,
  useAddHandleMutation,
  useArchiveHandleMutation,
  useCreateDivisionMutation,
  useGetCarrierQuery,
  useListDivisionHandlesQuery,
  useListDivisionsQuery,
  useUpdateCarrierMutation,
} from '@/services/api/carriersApiService';

import {
  type HandleType,
  HEALTH_CARRIER_TYPE_OPTIONS,
  type HealthCarrierType,
} from './types';

// --- Constants ---

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

const HANDLE_ICONS: Record<string, React.ElementType> = {
  PHONE: Phone,
  EMAIL: Mail,
  MAILING_ADDRESS: MapPin,
  FAX: Phone,
  WEBSITE: Building2,
};

const HANDLE_TYPE_LABELS: Record<HandleType, string> = {
  PHONE: 'Phone',
  EMAIL: 'Email',
  MAILING_ADDRESS: 'Mailing Address',
  FAX: 'Fax',
  WEBSITE: 'Website',
};

// --- Sub-components ---

const DivisionHandleList = ({
  carrierId,
  divisionId,
}: {
  carrierId: string;
  divisionId: string;
}) => {
  const { data: handles, isLoading } = useListDivisionHandlesQuery({
    carrierId,
    divisionId,
  });
  const [archiveHandle] = useArchiveHandleMutation();

  const handleDelete = async (handleId: string) => {
    try {
      await archiveHandle({ handleId }).unwrap();
      toast.success('Contact archived');
    } catch {
      toast.error('Failed to archive contact');
    }
  };

  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground p-2">
        Loading contacts...
      </div>
    );
  }

  if (!handles?.length) {
    return (
      <div className="text-xs text-muted-foreground italic p-2 bg-slate-50 rounded border border-dashed border-slate-200">
        No contacts in this division
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {handles.map((handle) => {
        const Icon = HANDLE_ICONS[handle.handleType] || Phone;
        return (
          <div
            key={handle.id}
            className="group flex items-center justify-between p-2 rounded-md bg-white border border-slate-100 hover:border-slate-300"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-1.5 bg-slate-100 rounded text-slate-500">
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate text-slate-900">
                  {handle.name || handle.handleType}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {handle.handleType === 'MAILING_ADDRESS'
                    ? `${handle.addressLine1}, ${handle.city}`
                    : handle.value}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
              onClick={() => handleDelete(handle.id)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        );
      })}
    </div>
  );
};

const AddContactForm = ({
  carrierId,
  divisionId,
  onCancel,
}: {
  carrierId: string;
  divisionId: string;
  onCancel: () => void;
}) => {
  const [addHandle] = useAddHandleMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [type, setType] = useState<HandleType>('PHONE');
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [addr1, setAddr1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');

  const handleSubmit = async () => {
    if (!name) {
      toast.error('Name is required');
      return;
    }
    if (type !== 'MAILING_ADDRESS' && !value) {
      toast.error('Value is required');
      return;
    }

    setIsSubmitting(true);
    try {
      // Pass divisionId directly - backend will auto-link the handle
      await addHandle({
        carrierId,
        divisionId, // <-- This fixes the "no default division" error
        handleType: type,
        name,
        value: type !== 'MAILING_ADDRESS' ? value : undefined,
        addressLine1: type === 'MAILING_ADDRESS' ? addr1 : undefined,
        city: type === 'MAILING_ADDRESS' ? city : undefined,
        state: type === 'MAILING_ADDRESS' ? state : undefined,
        zip: type === 'MAILING_ADDRESS' ? zip : undefined,
      }).unwrap();

      toast.success('Contact added');
      onCancel();
    } catch {
      toast.error('Failed to add contact');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
      <div className="flex gap-2">
        <Select value={type} onValueChange={(v) => setType(v as HandleType)}>
          <SelectTrigger className="w-[110px] h-8 text-xs bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(HANDLE_TYPE_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Label (e.g. Claims Dept)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-8 text-xs bg-white flex-1"
        />
      </div>

      {type === 'MAILING_ADDRESS' ? (
        <div className="space-y-2">
          <Input
            placeholder="Address Line 1"
            value={addr1}
            onChange={(e) => setAddr1(e.target.value)}
            className="h-8 text-xs bg-white"
          />
          <div className="flex gap-2">
            <Input
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="h-8 text-xs bg-white flex-1"
            />
            <Select value={state} onValueChange={setState}>
              <SelectTrigger className="w-[70px] h-8 text-xs bg-white">
                <SelectValue placeholder="St" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Zip"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              className="h-8 text-xs bg-white w-20"
            />
          </div>
        </div>
      ) : (
        <Input
          placeholder={type === 'EMAIL' ? 'email@example.com' : '555-0123'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="h-8 text-xs bg-white"
        />
      )}

      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          className="h-7 text-xs"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="h-7 text-xs"
        >
          Add Contact
        </Button>
      </div>
    </div>
  );
};

const DivisionRow = ({
  carrierId,
  division,
}: {
  carrierId: string;
  division: CarrierDivision;
}) => {
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [isExpanded, setIsExpanded] = useState(division.isDefault);

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden hover:border-slate-300">
      <button
        type="button"
        className="w-full flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 text-left"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
          <span className="font-medium text-sm text-slate-900">
            {division.divisionName}
          </span>
          {division.isDefault && (
            <Badge variant="secondary" className="text-[10px] h-5">
              Default
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1 text-primary hover:text-primary hover:bg-primary/10"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(true);
            setIsAddingContact(true);
          }}
        >
          <Plus className="w-3 h-3" />
          Add Contact
        </Button>
      </button>

      {isExpanded && (
        <div className="p-3 border-t border-slate-100 space-y-3">
          <DivisionHandleList carrierId={carrierId} divisionId={division.id} />

          {isAddingContact && (
            <AddContactForm
              carrierId={carrierId}
              divisionId={division.id}
              onCancel={() => setIsAddingContact(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

// --- Main Modal Component ---

interface EditCarrierModalProps {
  open: boolean;
  onClose: () => void;
  carrierId: string;
  onSaved?: (newName: string) => void;
}

export const EditCarrierModal: React.FC<EditCarrierModalProps> = ({
  open,
  onClose,
  carrierId,
  onSaved,
}) => {
  const { data: carrier, isLoading } = useGetCarrierQuery(carrierId, {
    skip: !open || !carrierId,
  });
  const { data: divisions } = useListDivisionsQuery(
    { carrierId },
    { skip: !open || !carrierId },
  );
  const [updateCarrier, { isLoading: isSaving }] = useUpdateCarrierMutation();
  const [createDivision] = useCreateDivisionMutation();

  // Form state
  const [name, setName] = useState('');
  const [isHealthCarrier, setIsHealthCarrier] = useState(false);
  const [healthCarrierType, setHealthCarrierType] =
    useState<HealthCarrierType | null>(null);
  const [showAddress, setShowAddress] = useState(false);
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');

  // New division state
  const [isAddingDivision, setIsAddingDivision] = useState(false);
  const [newDivisionName, setNewDivisionName] = useState('');

  // Initialize form from carrier
  useEffect(() => {
    if (carrier) {
      setName(carrier.name || '');
      setIsHealthCarrier(!!carrier.healthCarrierType);
      setHealthCarrierType(
        (carrier.healthCarrierType as HealthCarrierType) || null,
      );
      setAddressLine1(carrier.addressLine1 || '');
      setAddressLine2(carrier.addressLine2 || '');
      setCity(carrier.city || '');
      setState(carrier.state || '');
      setZip(carrier.zip || '');
      setShowAddress(
        !!(
          carrier.addressLine1 ||
          carrier.addressLine2 ||
          carrier.city ||
          carrier.state ||
          carrier.zip
        ),
      );
    }
  }, [carrier]);

  const handleSaveGeneral = async () => {
    if (!name.trim()) {
      toast.error('Carrier name is required');
      return;
    }

    try {
      await updateCarrier({
        carrierId,
        name: name.trim(),
        healthCarrierType: isHealthCarrier ? healthCarrierType : null,
        addressLine1: addressLine1.trim() || null,
        addressLine2: addressLine2.trim() || null,
        city: city.trim() || null,
        state: state || null,
        zip: zip.trim() || null,
      }).unwrap();

      toast.success('Carrier updated');
      onSaved?.(name.trim());
    } catch (error) {
      console.error('Failed to update carrier:', error);
      toast.error('Failed to update carrier');
    }
  };

  const handleAddDivision = async () => {
    if (!newDivisionName.trim()) return;
    try {
      await createDivision({
        carrierId,
        divisionName: newDivisionName.trim(),
      }).unwrap();
      toast.success('Division created');
      setNewDivisionName('');
      setIsAddingDivision(false);
    } catch {
      toast.error('Failed to create division');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            {carrier?.name || 'Edit Carrier'}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="text-sm text-muted-foreground">
              Loading carrier data...
            </div>
          </div>
        ) : (
          <Tabs
            defaultValue="general"
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div className="px-6 bg-white border-b border-slate-200">
              <TabsList className="w-full justify-start h-auto p-0 bg-transparent gap-6">
                <TabsTrigger
                  value="general"
                  className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:text-primary border-b-2 border-transparent rounded-none px-1 py-3 font-medium"
                >
                  General Info
                </TabsTrigger>
                <TabsTrigger
                  value="structure"
                  className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:text-primary border-b-2 border-transparent rounded-none px-1 py-3 font-medium"
                >
                  Divisions & Contacts
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto">
              <TabsContent value="general" className="m-0 p-6 space-y-6">
                {/* Carrier Name */}
                <div className="space-y-2">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="text-sm font-medium text-slate-700">
                    Carrier Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Acme Insurance"
                  />
                </div>

                {/* Health Carrier Checkbox */}
                <div className="space-y-4">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100">
                    <input
                      type="checkbox"
                      checked={isHealthCarrier}
                      onChange={(e) => {
                        setIsHealthCarrier(e.target.checked);
                        if (!e.target.checked) setHealthCarrierType(null);
                      }}
                      className="mt-1 w-4 h-4 text-primary rounded border-slate-300 focus:ring-primary/20"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-slate-900 block">
                        Health Insurance Carrier
                      </span>
                      <span className="text-xs text-slate-500 block mt-0.5">
                        Check this for Medicare, Medicaid, and commercial health
                        plans. Leave unchecked for auto/property.
                      </span>

                      {isHealthCarrier && (
                        <div className="mt-3">
                          <Select
                            value={healthCarrierType || ''}
                            onValueChange={(v) =>
                              setHealthCarrierType(v as HealthCarrierType)
                            }
                          >
                            <SelectTrigger className="bg-white w-full sm:w-[300px]">
                              <SelectValue placeholder="Select carrier type..." />
                            </SelectTrigger>
                            <SelectContent>
                              {HEALTH_CARRIER_TYPE_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {/* Address */}
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setShowAddress(!showAddress)}
                    className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
                  >
                    {showAddress ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                    Corporate Address{' '}
                    <span className="text-slate-400 font-normal">
                      (optional)
                    </span>
                  </button>

                  {showAddress && (
                    <div className="space-y-3 pl-6">
                      <Input
                        placeholder="Address Line 1"
                        value={addressLine1}
                        onChange={(e) => setAddressLine1(e.target.value)}
                      />
                      <Input
                        placeholder="Address Line 2"
                        value={addressLine2}
                        onChange={(e) => setAddressLine2(e.target.value)}
                      />
                      <div className="flex gap-3">
                        <Input
                          placeholder="City"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          className="flex-1"
                        />
                        <Select value={state} onValueChange={setState}>
                          <SelectTrigger className="w-[100px]">
                            <SelectValue placeholder="State" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Zip"
                          value={zip}
                          onChange={(e) => setZip(e.target.value)}
                          className="w-28"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100">
                  <Button
                    onClick={handleSaveGeneral}
                    disabled={isSaving}
                    className="w-full sm:w-auto"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="structure" className="m-0 p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">
                      Divisions & Contacts
                    </h3>
                    <p className="text-sm text-slate-500">
                      Manage claims, legal, and other departments.
                    </p>
                  </div>
                  {!isAddingDivision && (
                    <Button
                      onClick={() => setIsAddingDivision(true)}
                      size="sm"
                      variant="outline"
                      className="gap-2"
                    >
                      <Plus className="w-4 h-4" /> New Division
                    </Button>
                  )}
                </div>

                {isAddingDivision && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 flex gap-3">
                    <div className="p-2 bg-white rounded shadow-sm">
                      <LayoutList className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="space-y-1">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          htmlFor="new-div-name"
                          className="text-xs font-semibold uppercase text-blue-700 tracking-wide"
                        >
                          Division Name
                        </label>
                        <Input
                          id="new-div-name"
                          placeholder="e.g. Bodily Injury Claims"
                          value={newDivisionName}
                          onChange={(e) => setNewDivisionName(e.target.value)}
                          className="bg-white"
                          autoFocus
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsAddingDivision(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleAddDivision}
                          disabled={!newDivisionName.trim()}
                        >
                          Create Division
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {divisions?.map((division) => (
                    <DivisionRow
                      key={division.id}
                      carrierId={carrierId}
                      division={division}
                    />
                  ))}
                  {divisions?.length === 0 && (
                    <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-muted-foreground font-medium">
                        No divisions yet
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Create a division to start adding contacts.
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        )}

        <div className="flex-shrink-0 p-4 bg-white border-t border-slate-200 flex justify-end">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
